import React from "react";
import { useObjectUrl } from "../hooks/useObjectUrl";
import { Photo, SyncStatus } from "../types/Photo";

interface PhotoThumbnailProps {
  photo: Photo;
  onDelete: (photo: Photo) => void;
  onPreview: (photo: Photo) => void;
}

/**
 * Une photo du produit ouvert. Seul l'échec se voit : une photo déjà envoyée
 * ne porte aucune marque particulière, la pastille du produit dit déjà où en
 * est l'envoi, et rien ici ne se coche — toute photo prise part avec le produit.
 */
const PhotoThumbnail: React.FC<PhotoThumbnailProps> = ({
  photo,
  onDelete,
  onPreview,
}) => {
  const imageUrl = useObjectUrl(photo.thumbnailBlob);

  const hasFailed = photo.syncStatus === SyncStatus.FAILED;

  return (
    <div
      className={`relative group aspect-square rounded-xl overflow-hidden border-2 transition-all ${hasFailed ? "border-error shadow-md" : "border-base-300"}`}
    >
      {/* Image */}
      <img
        src={imageUrl}
        alt={photo.filename}
        className="w-full h-full object-cover cursor-pointer"
        onClick={() => onPreview(photo)}
      />

      {/* Suppression : geste d'exception, toujours atteignable au doigt — le
          survol n'existe pas sur le téléphone de l'opérateur. */}
      <button
        className="absolute top-2 right-2 btn btn-circle btn-xs btn-error"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(photo);
        }}
        aria-label={`Supprimer ${photo.filename}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      {/* Bandeau du bas : la cause de l'échec prend la place du nom de fichier */}
      <div
        className={`absolute bottom-0 left-0 right-0 text-[10px] p-1 truncate ${hasFailed ? "bg-error text-error-content font-bold" : "bg-black/40 text-white"}`}
        title={hasFailed ? photo.lastError : photo.filename}
      >
        {hasFailed
          ? `Échec : ${photo.lastError ?? "cause inconnue"}`
          : photo.filename}
      </div>
    </div>
  );
};

export default PhotoThumbnail;
